import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  Button,
  Row,
  Col,
  Select,
  Input,
  InputNumber,
  Switch,
  Tag,
  Space,
  Divider,
  Card,
  Tooltip,
  Segmented,
  List,
  Avatar,
  Badge,
  Spin,
  message
} from 'antd';
import {
  VideoCameraOutlined,
  DesktopOutlined,
  AudioOutlined,
  AudioMutedOutlined,
  FolderOpenOutlined,
  PlayCircleOutlined,
  StopOutlined,
  ReloadOutlined,
  SettingOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  AppstoreOutlined,
  SwapOutlined,
  ArrowLeftOutlined,
  SendOutlined,
  UserOutlined,
  ClockCircleOutlined,
  MutedOutlined,
  SoundOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
  UpOutlined,
  DownOutlined
} from '@ant-design/icons';
import {
  scheduleRTMPSession,
  sendWebBroadcastChunk,
  stopWebBroadcast,
  fetchWebBroadcastLocalFile,
  updateRTMPSessionStatus
} from '../../../Service/Schedule/rtmpSession_API';
import {
  fetchChatMessages,
  sendChatMessage,
  sendViewerHeartbeat,
  fetchActiveViewers
} from '../../../Service/LiveSession_API/liveSessionApi';
import { fetchCourses } from '../../../Service/Courses_API/coursesApi';
import { fetchModules } from '../../../Service/Modules_API/modulesApi';
import { fetchInstructors } from '../../../Service/Instructor_API/instructorApi';
import { fetchVideoUploadInitApi } from '../../../Service/VideoIndex_API/videoIndexApi';
import { SERVER_URL } from '../../../Service/apiConfig';
import { getEncryptedLiveStreamUrl } from '../../../Utils/videoEncryption';

const { Option } = Select;

// flv.js is loaded via CDN in public/index.html
const getFlvjs = () => {
  if (typeof window !== 'undefined' && window.flvjs && window.flvjs.isSupported) {
    return window.flvjs;
  }
  return null;
};

const VIDEO_BITRATE_OPTIONS = [
  { label: '500 kbps (Low / Mobile)', value: '500k', bps: 500000 },
  { label: '800 kbps (Smooth SD)', value: '800k', bps: 800000 },
  { label: '1000 kbps (Standard 720p)', value: '1000k', bps: 1000000 },
  { label: '1500 kbps (High 720p)', value: '1500k', bps: 1500000 },
  { label: '2500 kbps (Full HD 1080p)', value: '2500k', bps: 2500000 },
  { label: '4000 kbps (Ultra HD 1080p)', value: '4000k', bps: 4000000 }
];

const AUDIO_BITRATE_OPTIONS = [
  { label: '64 kbps (Voice)', value: '64k', bps: 64000 },
  { label: '96 kbps (Standard)', value: '96k', bps: 96000 },
  { label: '128 kbps (High Quality)', value: '128k', bps: 128000 },
  { label: '192 kbps (Studio Quality)', value: '192k', bps: 192000 }
];

const LAYOUT_PRESETS = [
  {
    id: 'pip_br',
    name: 'PiP Bottom-Right',
    desc: 'Screen + Webcam Bottom-Right',
    mode: 'both'
  },
  {
    id: 'pip_bl',
    name: 'PiP Bottom-Left',
    desc: 'Screen + Webcam Bottom-Left',
    mode: 'both'
  },
  {
    id: 'pip_tr',
    name: 'PiP Top-Right',
    desc: 'Screen + Webcam Top-Right',
    mode: 'both'
  },
  {
    id: 'pip_tl',
    name: 'PiP Top-Left',
    desc: 'Screen + Webcam Top-Left',
    mode: 'both'
  },
  {
    id: 'circle_br',
    name: 'Circle Bubble',
    desc: 'Screen + Round Webcam Bubble',
    mode: 'both'
  },
  {
    id: 'split_70_30',
    name: 'Lecture 70 / 30',
    desc: '70% Slides + 30% Instructor',
    mode: 'both'
  },
  {
    id: 'split_50_50',
    name: 'Side-by-Side 50/50',
    desc: 'Equal Split Screen & Webcam',
    mode: 'both'
  },
  {
    id: 'webcam_pip',
    name: 'Webcam + Screen PiP',
    desc: 'Full Webcam + Corner Screen',
    mode: 'both'
  },
  {
    id: 'screen_only',
    name: 'Screen Only',
    desc: 'Full Window / Screen Capture',
    mode: 'screen'
  },
  {
    id: 'webcam_only',
    name: 'Webcam Only',
    desc: 'Full Instructor Camera',
    mode: 'webcam'
  }
];

const PIP_SIZE_SCALE = {
  Small: 0.8,
  Medium: 1.0,
  Large: 1.28
};

// Mini SVG visual icon for each layout preset card
const renderLayoutThumbnail = (layoutId, isActive) => {
  const strokeColor = isActive ? '#8b5cf6' : '#64748b';
  const screenFill = isActive ? 'rgba(59, 130, 246, 0.28)' : 'rgba(148, 163, 184, 0.18)';
  const camFill = isActive ? '#8b5cf6' : '#64748b';

  return (
    <svg
      width="56"
      height="34"
      viewBox="0 0 56 34"
      style={{ display: 'block', margin: '0 auto 4px auto', borderRadius: 4 }}
    >
      <rect
        x="1"
        y="1"
        width="54"
        height="32"
        rx="4"
        fill={screenFill}
        stroke={strokeColor}
        strokeWidth="1.5"
      />
      {layoutId === 'pip_br' && (
        <rect x="35" y="19" width="17" height="11" rx="2" fill={camFill} />
      )}
      {layoutId === 'pip_bl' && (
        <rect x="4" y="19" width="17" height="11" rx="2" fill={camFill} />
      )}
      {layoutId === 'pip_tr' && (
        <rect x="35" y="4" width="17" height="11" rx="2" fill={camFill} />
      )}
      {layoutId === 'pip_tl' && (
        <rect x="4" y="4" width="17" height="11" rx="2" fill={camFill} />
      )}
      {layoutId === 'circle_br' && (
        <circle cx="44" cy="23" r="7" fill={camFill} />
      )}
      {layoutId === 'split_70_30' && (
        <>
          <line x1="37" y1="2" x2="37" y2="32" stroke={strokeColor} strokeWidth="1.5" />
          <rect x="39" y="8" width="13" height="18" rx="2" fill={camFill} />
        </>
      )}
      {layoutId === 'split_50_50' && (
        <>
          <line x1="28" y1="2" x2="28" y2="32" stroke={strokeColor} strokeWidth="1.5" />
          <rect x="30" y="5" width="22" height="24" rx="2" fill={camFill} />
        </>
      )}
      {layoutId === 'webcam_pip' && (
        <>
          <rect x="2" y="2" width="52" height="30" rx="3" fill={camFill} fillOpacity="0.45" />
          <rect x="35" y="19" width="17" height="11" rx="2" fill="#38bdf8" />
        </>
      )}
      {layoutId === 'screen_only' && (
        <text x="28" y="20" textAnchor="middle" fontSize="8" fill={strokeColor} fontWeight="bold">
          SCREEN
        </text>
      )}
      {layoutId === 'webcam_only' && (
        <>
          <rect x="2" y="2" width="52" height="30" rx="3" fill={camFill} fillOpacity="0.45" />
          <circle cx="28" cy="17" r="6" fill={camFill} />
        </>
      )}
    </svg>
  );
};

// Student / Participant Live Webcam Card (matching Classroom Stream)
const ParticipantWebcamCard = ({ viewer, onHide }) => {
  const [muted, setMuted] = useState(true);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamStatus, setStreamStatus] = useState('connecting');
  const [showControls, setShowControls] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const flvPlayerRef = useRef(null);

  const studentId = viewer.login_id || viewer.id || viewer.session_viewers_id;
  const streamKey = `webcam_${studentId}`;
  const flvUrl = getEncryptedLiveStreamUrl(streamKey, SERVER_URL);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = muted;
    }
  }, [muted]);

  useEffect(() => {
    let retryTimeout = null;
    let healthInterval = null;
    let isDestroyed = false;

    const destroyPlayer = () => {
      if (flvPlayerRef.current) {
        try {
          flvPlayerRef.current.pause();
          flvPlayerRef.current.unload();
          flvPlayerRef.current.detachMediaElement();
          flvPlayerRef.current.destroy();
        } catch (e) {}
        flvPlayerRef.current = null;
      }
    };

    const initWebcamStream = () => {
      if (isDestroyed) return;
      const flv = getFlvjs();
      if (!flv || !flv.isSupported || !flv.isSupported()) {
        retryTimeout = setTimeout(initWebcamStream, 300);
        return;
      }
      if (!videoRef.current) {
        retryTimeout = setTimeout(initWebcamStream, 200);
        return;
      }

      setStreamStatus('connecting');
      destroyPlayer();

      if (videoRef.current) {
        videoRef.current.muted = muted;
        videoRef.current.defaultMuted = true;
        videoRef.current.playsInline = true;
      }

      try {
        const flvPlayer = flv.createPlayer(
          {
            type: 'flv',
            url: flvUrl,
            isLive: true,
            cors: true,
            hasAudio: true,
            hasVideo: true
          },
          {
            enableWorker: false,
            enableStashBuffer: false,
            stashInitialSize: 128,
            isLive: true,
            autoCleanupSourceBuffer: true,
            autoCleanupMaxBackwardDuration: 3,
            autoCleanupMinBackwardDuration: 1,
            lazyLoad: false
          }
        );

        flvPlayer.attachMediaElement(videoRef.current);
        flvPlayer.load();

        if (videoRef.current) {
          videoRef.current.muted = true;
          const playPromise = videoRef.current.play();
          if (playPromise && typeof playPromise.then === 'function') {
            playPromise.catch(() => {});
          }
        }

        if (flv.Events) {
          const markLive = () => {
            if (!isDestroyed) {
              setIsStreaming(true);
              setStreamStatus('live');
            }
          };
          if (flv.Events.MEDIA_INFO) flvPlayer.on(flv.Events.MEDIA_INFO, markLive);
          if (flv.Events.METADATA_ARRIVED) flvPlayer.on(flv.Events.METADATA_ARRIVED, markLive);
          if (flv.Events.STATISTICS_INFO) {
            flvPlayer.on(flv.Events.STATISTICS_INFO, (info) => {
              if (info && info.speed > 0) markLive();
            });
          }
          if (flv.Events.ERROR) {
            flvPlayer.on(flv.Events.ERROR, () => {
              if (!isDestroyed) {
                setIsStreaming(false);
                setStreamStatus('retrying');
                destroyPlayer();
                retryTimeout = setTimeout(initWebcamStream, 1500);
              }
            });
          }
        }

        flvPlayerRef.current = flvPlayer;
      } catch (err) {
        if (!isDestroyed) {
          setStreamStatus('retrying');
          retryTimeout = setTimeout(initWebcamStream, 1500);
        }
      }
    };

    initWebcamStream();

    healthInterval = setInterval(() => {
      if (isDestroyed || !videoRef.current) return;
      const v = videoRef.current;
      if (v.buffered && v.buffered.length > 0) {
        const end = v.buffered.end(v.buffered.length - 1);
        if (end - v.currentTime > 1.2) {
          v.currentTime = end - 0.1;
        }
        if (!isStreaming && v.currentTime > 0) {
          setIsStreaming(true);
          setStreamStatus('live');
        }
      }
    }, 1000);

    return () => {
      isDestroyed = true;
      if (retryTimeout) clearTimeout(retryTimeout);
      if (healthInterval) clearInterval(healthInterval);
      destroyPlayer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewer.login_id, viewer.id, viewer.session_viewers_id, viewer.user_name]);

  const handleManualReload = (e) => {
    if (e) e.stopPropagation();
    setIsStreaming(false);
    setStreamStatus('connecting');
    if (flvPlayerRef.current) {
      try {
        flvPlayerRef.current.unload();
        flvPlayerRef.current.load();
        if (videoRef.current) videoRef.current.play().catch(() => {});
      } catch (_) {}
    }
  };

  const toggleFullscreen = (e) => {
    if (e) e.stopPropagation();
    if (!containerRef.current) return;
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().then(() => setIsFullscreen(true)).catch(() => {});
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false)).catch(() => {});
    }
  };

  return (
    <div
      ref={containerRef}
      onMouseEnter={() => setShowControls(true)}
      onMouseLeave={() => setShowControls(false)}
      style={{
        position: 'relative',
        width: '100%',
        aspectRatio: '16 / 9',
        borderRadius: 10,
        overflow: 'hidden',
        background: '#0f172a',
        border: isStreaming ? '2px solid rgba(16, 185, 129, 0.9)' : '2px solid rgba(245, 158, 11, 0.6)',
        boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted={muted}
        controlsList="nodownload"
        disablePictureInPicture
        onContextMenu={(e) => e.preventDefault()}
        onPlaying={() => {
          setIsStreaming(true);
          setStreamStatus('live');
        }}
        onLoadedData={() => {
          setIsStreaming(true);
          setStreamStatus('live');
        }}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          zIndex: 2,
          opacity: isStreaming ? 1 : 0.1,
          transition: 'opacity 0.3s ease'
        }}
      />

      {!isStreaming && (
        <div style={{ zIndex: 1, textAlign: 'center', color: '#94a3b8', padding: 8 }}>
          <Spin size="small" />
          <div style={{ fontSize: 11, marginTop: 6, fontWeight: 500 }}>
            {streamStatus === 'retrying' ? 'Reconnecting FLV...' : 'Connecting FLV Stream...'}
          </div>
        </div>
      )}

      <div
        style={{
          position: 'absolute',
          bottom: 6,
          left: 6,
          right: 6,
          background: 'rgba(15, 23, 42, 0.85)',
          backdropFilter: 'blur(4px)',
          color: '#fff',
          padding: '3px 8px',
          borderRadius: 6,
          fontSize: 11,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          zIndex: 3,
          border: '1px solid rgba(255,255,255,0.1)'
        }}
      >
        <span
          style={{
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            maxWidth: 110,
            fontWeight: 600
          }}
        >
          {viewer.user_name || `Student #${studentId}`}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span
            style={{
              display: 'inline-block',
              width: 7,
              height: 7,
              borderRadius: '50%',
              background: isStreaming ? '#10b981' : '#f59e0b'
            }}
          />
          <span
            style={{
              fontSize: 9,
              opacity: 0.8,
              textTransform: 'uppercase',
              fontWeight: 700,
              letterSpacing: 0.5
            }}
          >
            {isStreaming ? 'LIVE' : streamStatus.toUpperCase()}
          </span>
        </div>
      </div>

      <div
        style={{
          position: 'absolute',
          top: 6,
          right: 6,
          display: 'flex',
          gap: 4,
          zIndex: 4,
          opacity: showControls ? 1 : 0,
          transition: 'opacity 0.2s ease',
          background: 'rgba(0,0,0,0.6)',
          backdropFilter: 'blur(4px)',
          padding: '3px 5px',
          borderRadius: 6
        }}
      >
        <Tooltip title={muted ? 'Unmute Audio' : 'Mute Audio'}>
          <Button
            type="text"
            size="small"
            icon={
              muted ? (
                <MutedOutlined style={{ color: '#ff4d4f' }} />
              ) : (
                <SoundOutlined style={{ color: '#52c41a' }} />
              )
            }
            onClick={(e) => {
              e.stopPropagation();
              setMuted(!muted);
            }}
            style={{ color: '#fff', padding: '0 4px', height: 22, width: 22, minWidth: 22 }}
          />
        </Tooltip>

        <Tooltip title="Reload Stream">
          <Button
            type="text"
            size="small"
            icon={<ClockCircleOutlined style={{ color: '#1890ff' }} />}
            onClick={handleManualReload}
            style={{ color: '#fff', padding: '0 4px', height: 22, width: 22, minWidth: 22 }}
          />
        </Tooltip>

        {onHide && (
          <Tooltip title="Hide Participant Video">
            <Button
              type="text"
              size="small"
              icon={<EyeInvisibleOutlined style={{ color: '#ff4d4f' }} />}
              onClick={(e) => {
                e.stopPropagation();
                onHide(studentId);
              }}
              style={{ color: '#fff', padding: '0 4px', height: 22, width: 22, minWidth: 22 }}
            />
          </Tooltip>
        )}

        <Tooltip title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen View'}>
          <Button
            type="text"
            size="small"
            icon={
              isFullscreen ? (
                <FullscreenExitOutlined style={{ color: '#fff' }} />
              ) : (
                <FullscreenOutlined style={{ color: '#fff' }} />
              )
            }
            onClick={toggleFullscreen}
            style={{ color: '#fff', padding: '0 4px', height: 22, width: 22, minWidth: 22 }}
          />
        </Tooltip>
      </div>
    </div>
  );
};

const WebBroadcastModal = ({
  open,
  isOpen,
  onClose,
  session,
  course,
  module,
  courseName,
  moduleName,
  instructorsList: propInstructors,
  defaultSlNo = 1,
  onSessionUpdated
}) => {
  const pageVisible = Boolean(open || isOpen);

  // Current logged-in user for Classroom Chat
  const authUser = useSelector((state) => state.auth?.user);
  const localUser = (() => {
    try {
      return JSON.parse(localStorage.getItem('user'));
    } catch (_) {
      return null;
    }
  })();
  const currentUser = authUser || localUser || { id: 1, username: 'Studio Host', role: 'STUDIO' };
  const currentUserId = currentUser.login_id || currentUser.id || 1;
  const currentUsername =
    currentUser.username || currentUser.login_name || currentUser.name || 'Studio Host';
  const currentUserRole = (currentUser.role || 'STUDIO').toUpperCase();

  // Active session (either passed from a scheduled row or created when clicking "Start Live & Record")
  const [activeSession, setActiveSession] = useState(session || null);
  const activeSessionRef = useRef(session || null);

  // Quick-session fields when opened from top-level "Start From Web" button (without pre-existing session)
  const propCourseId = course?.courses_id || session?.courses_id || '';
  const propModuleId = module?.modules_id || session?.modules_id || '';
  const [selectedCourseId, setSelectedCourseId] = useState(propCourseId);
  const [selectedModuleId, setSelectedModuleId] = useState(propModuleId);
  const [coursesList, setCoursesList] = useState([]);
  const [modulesList, setModulesList] = useState([]);
  const [instructorsList, setInstructorsList] = useState(propInstructors || []);
  const [quickSlNo, setQuickSlNo] = useState(defaultSlNo || 1);
  const [quickTitle, setQuickTitle] = useState('');
  const [quickInstructorId, setQuickInstructorId] = useState(undefined);

  // Device lists & selection
  const [videoDevices, setVideoDevices] = useState([]);
  const [audioDevices, setAudioDevices] = useState([]);
  const [selectedVideoDeviceId, setSelectedVideoDeviceId] = useState('');
  const [selectedAudioDeviceId, setSelectedAudioDeviceId] = useState('');
  const [includeSystemAudio, setIncludeSystemAudio] = useState(true);
  const [isWindowActive, setIsWindowActive] = useState(false);
  const [windowLabel, setWindowLabel] = useState('');
  const [isWebcamActive, setIsWebcamActive] = useState(false);

  // Scene Layout & Overlay states
  const [layoutPreset, setLayoutPreset] = useState('pip_br');
  const [pipSize, setPipSize] = useState('Medium'); // 'Small' | 'Medium' | 'Large'
  const [mirrorWebcam, setMirrorWebcam] = useState(false);
  const [showLowerThird, setShowLowerThird] = useState(true);

  // Refs for instant 30fps compositor updates
  const layoutPresetRef = useRef('pip_br');
  const pipSizeRef = useRef('Medium');
  const mirrorWebcamRef = useRef(false);
  const showLowerThirdRef = useRef(true);
  const bannerTextRef = useRef({ title: '', subtitle: '' });

  // Classroom stream bitrate controls
  const [videoBitrate, setVideoBitrate] = useState('1500k');
  const [audioBitrate, setAudioBitrate] = useState('128k');

  // Live toggle controls
  const [isMicMuted, setIsMicMuted] = useState(false);
  const [isWebcamEnabled, setIsWebcamEnabled] = useState(true);
  const isWebcamEnabledRef = useRef(true);

  // Local system recording controls
  const [enableLocalRecording, setEnableLocalRecording] = useState(true);
  const [localRecordingFormat, setLocalRecordingFormat] = useState('mkv'); // 'mkv' | 'flv'
  const [localDirName, setLocalDirName] = useState('');
  const [localSubfolderPath, setLocalSubfolderPath] = useState('');
  const [localCustomFileName, setLocalCustomFileName] = useState('');
  const [localBytesWritten, setLocalBytesWritten] = useState(0);
  const [savedLocalFilePath, setSavedLocalFilePath] = useState('');

  // Broadcast telemetry & states
  const [isPreviewActive, setIsPreviewActive] = useState(false);
  const [isBroadcasting, setIsBroadcasting] = useState(false);
  const [isStopping, setIsStopping] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [chunksSent, setChunksSent] = useState(0);
  const [serverBytesSent, setServerBytesSent] = useState(0);

  // Classroom Chat & Attending Participants (User Webcams) states
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [activeViewers, setActiveViewers] = useState([]);
  const [hiddenParticipantIds, setHiddenParticipantIds] = useState([]);
  const [isParticipantsMinimized, setIsParticipantsMinimized] = useState(false);
  const chatContainerRef = useRef(null);
  const chatPollIntervalRef = useRef(null);

  // Refs for canvas compositor, streams, and recorders
  const previewCanvasRef = useRef(null);
  const webcamVideoRef = useRef(null);
  const screenVideoRef = useRef(null);
  const webcamStreamRef = useRef(null);
  const screenStreamRef = useRef(null);
  const micStreamRef = useRef(null);
  const audioContextRef = useRef(null);
  const mixedAudioDestRef = useRef(null);
  const animationFrameRef = useRef(null);
  const workerTickerRef = useRef(null);
  const fallbackIntervalRef = useRef(null);
  const lastRenderTimeRef = useRef(0);
  const canvasStreamRef = useRef(null);

  const mediaRecorderRef = useRef(null);
  const chunkSeqRef = useRef(0);
  const uploadChainRef = useRef(Promise.resolve());
  const localChunksRef = useRef([]);
  const localDirHandleRef = useRef(null);
  const localFileHandleRef = useRef(null);
  const localWritableRef = useRef(null);
  const timerIntervalRef = useRef(null);
  const isBroadcastingRef = useRef(false);

  // Sync refs
  useEffect(() => {
    layoutPresetRef.current = layoutPreset;
  }, [layoutPreset]);

  useEffect(() => {
    pipSizeRef.current = pipSize;
  }, [pipSize]);

  useEffect(() => {
    mirrorWebcamRef.current = mirrorWebcam;
  }, [mirrorWebcam]);

  useEffect(() => {
    showLowerThirdRef.current = showLowerThird;
  }, [showLowerThird]);

  useEffect(() => {
    isWebcamEnabledRef.current = isWebcamEnabled;
  }, [isWebcamEnabled]);

  useEffect(() => {
    const currentSess = activeSession || session;
    const titleStr = currentSess?.video_title || quickTitle || 'Live Classroom Session';
    const instObj = instructorsList.find(
      (i) => Number(i.instructor_id) === Number(currentSess?.instructor_id || quickInstructorId)
    );
    const instructorName =
      currentSess?.instructor_name || instObj?.instructor_name || '';
    const modStr =
      module?.modules_name ||
      moduleName ||
      currentSess?.modules_name ||
      course?.courses_name ||
      courseName ||
      '';
    const subParts = [instructorName, modStr].filter(Boolean);
    bannerTextRef.current = {
      title: titleStr,
      subtitle: subParts.join(' • ')
    };
  }, [
    activeSession,
    session,
    quickTitle,
    quickInstructorId,
    instructorsList,
    module,
    moduleName,
    course,
    courseName
  ]);

  // Auto-scroll Classroom Chat to latest message
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatMessages]);

  // Poll Classroom Chat & Attending Participants (User Webcams) whenever a session ID exists
  const currentSessionId =
    activeSession?.rtmp_session_id ||
    activeSession?.session_id ||
    session?.rtmp_session_id ||
    session?.session_id;

  const pollClassroomChatAndViewers = useCallback(
    async (sId) => {
      if (!sId) return;
      try {
        await sendViewerHeartbeat(sId, {
          login_id: currentUserId,
          user_name: currentUsername,
          role: currentUserRole,
          webcam_frame: null,
          audio_chunk: null
        }).catch(() => {});

        const [chats, viewers] = await Promise.all([
          fetchChatMessages(sId).catch(() => []),
          fetchActiveViewers(sId).catch(() => [])
        ]);
        if (Array.isArray(chats)) setChatMessages(chats);
        if (Array.isArray(viewers)) setActiveViewers(viewers);
      } catch (err) {
        console.warn('Classroom chat/viewers poll warning:', err);
      }
    },
    [currentUserId, currentUsername, currentUserRole]
  );

  useEffect(() => {
    if (!pageVisible || !currentSessionId) return;
    pollClassroomChatAndViewers(currentSessionId);
    chatPollIntervalRef.current = setInterval(() => {
      pollClassroomChatAndViewers(currentSessionId);
    }, 2000);

    return () => {
      if (chatPollIntervalRef.current) {
        clearInterval(chatPollIntervalRef.current);
        chatPollIntervalRef.current = null;
      }
    };
  }, [pageVisible, currentSessionId, pollClassroomChatAndViewers]);

  const handleSendChat = async () => {
    if (!chatInput.trim()) return;
    if (!currentSessionId) {
      message.info('Start the live broadcast first to activate the classroom chat.');
      return;
    }
    const msg = chatInput.trim();
    setChatInput('');
    try {
      await sendChatMessage(currentSessionId, {
        login_id: currentUserId,
        user_name: currentUsername,
        role: currentUserRole,
        chat_msg: msg
      });
      pollClassroomChatAndViewers(currentSessionId);
    } catch (err) {
      message.error('Failed to send chat message.');
    }
  };

  const handleHideParticipant = (studentId) => {
    const sId = String(studentId);
    setHiddenParticipantIds((prev) => (prev.includes(sId) ? prev : [...prev, sId]));
    message.info('Participant video hidden from view.');
  };

  // Sync session & defaults when page opens
  useEffect(() => {
    if (!pageVisible) return;
    setActiveSession(session || null);
    activeSessionRef.current = session || null;

    const cId = course?.courses_id || session?.courses_id || '';
    const mId = module?.modules_id || session?.modules_id || '';
    setSelectedCourseId(cId);
    setSelectedModuleId(mId);
    setQuickSlNo(defaultSlNo || 1);

    if (session) {
      const sId = session.rtmp_session_id || session.session_id || 'live';
      const safeTitle = (session.video_title || 'session').replace(/[^a-zA-Z0-9_-]/g, '_');
      setLocalCustomFileName(
        `session_${sId}_${safeTitle}_${new Date().toISOString().slice(0, 10)}.${localRecordingFormat}`
      );
    } else {
      const defaultTitleStr = `Live Web Session - ${new Date().toLocaleDateString()}`;
      setQuickTitle(defaultTitleStr);
      const safeTitle = defaultTitleStr.replace(/[^a-zA-Z0-9_-]/g, '_');
      setLocalCustomFileName(
        `web_session_${safeTitle}_${new Date().toISOString().slice(0, 10)}.${localRecordingFormat}`
      );
    }
  }, [pageVisible, session, course, module, defaultSlNo, localRecordingFormat]);

  // Update default filename when quickTitle or localRecordingFormat changes
  useEffect(() => {
    setLocalCustomFileName((prev) => {
      if (!prev) {
        const safeTitle = (quickTitle || 'live_session').replace(/[^a-zA-Z0-9_-]/g, '_');
        return `web_session_${safeTitle}_${new Date().toISOString().slice(0, 10)}.${localRecordingFormat}`;
      }
      const base = prev.replace(/\.(webm|mkv|flv)$/i, '');
      if (!session && quickTitle) {
        const safeTitle = quickTitle.replace(/[^a-zA-Z0-9_-]/g, '_');
        return `web_session_${safeTitle}_${new Date().toISOString().slice(0, 10)}.${localRecordingFormat}`;
      }
      return `${base}.${localRecordingFormat}`;
    });
  }, [session, quickTitle, localRecordingFormat]);

  // Load instructors if not passed via props
  useEffect(() => {
    if (!pageVisible) return;
    if (propInstructors && propInstructors.length > 0) {
      setInstructorsList(propInstructors);
      return;
    }
    fetchInstructors()
      .then((list) => {
        if (Array.isArray(list)) setInstructorsList(list);
      })
      .catch(() => {});
  }, [pageVisible, propInstructors]);

  // Load courses if opened without a preselected course
  useEffect(() => {
    if (!pageVisible || session || propCourseId) return;
    fetchCourses()
      .then((res) => {
        const list = Array.isArray(res?.courses)
          ? res.courses
          : Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res)
          ? res
          : [];
        setCoursesList(list);
        if (!selectedCourseId && list.length > 0) {
          setSelectedCourseId(list[0].courses_id);
        }
      })
      .catch(() => {});
  }, [pageVisible, session, propCourseId, selectedCourseId]);

  // Load modules when selectedCourseId changes
  useEffect(() => {
    if (!pageVisible || session || propModuleId || !selectedCourseId) return;
    fetchModules(selectedCourseId)
      .then((res) => {
        const list = Array.isArray(res?.modules)
          ? res.modules
          : Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res)
          ? res
          : [];
        setModulesList(list);
        if (list.length > 0) {
          setSelectedModuleId(list[0].modules_id);
        } else {
          setSelectedModuleId('');
        }
      })
      .catch(() => {});
  }, [pageVisible, session, propModuleId, selectedCourseId]);

  // Fetch next video serial number to continue Sl No. whenever selectedModuleId changes or is set
  useEffect(() => {
    if (!pageVisible || session || !selectedModuleId) return;
    let isMounted = true;
    fetchVideoUploadInitApi(selectedModuleId)
      .then((res) => {
        if (isMounted && res?.next_vid_cat_id !== undefined && res?.next_vid_cat_id !== null) {
          setQuickSlNo(Number(res.next_vid_cat_id));
        }
      })
      .catch((err) => {
        console.warn('Failed to fetch next Sl No. for module:', err);
      });
    return () => {
      isMounted = false;
    };
  }, [pageVisible, session, selectedModuleId]);

  // Enumerate webcam and audio input devices
  const refreshDevices = useCallback(async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
      return;
    }
    try {
      let tempStream = null;
      try {
        const initial = await navigator.mediaDevices.enumerateDevices();
        const hasLabels = initial.some((d) => d.label && d.label.trim() !== '');
        if (!hasLabels) {
          tempStream = await navigator.mediaDevices.getUserMedia({
            video: true,
            audio: true
          });
        }
      } catch (_) {
        try {
          tempStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        } catch (__) {
          // ignore
        }
      } finally {
        if (tempStream) {
          tempStream.getTracks().forEach((t) => t.stop());
        }
      }

      const devices = await navigator.mediaDevices.enumerateDevices();
      const vDevs = devices.filter((d) => d.kind === 'videoinput');
      const aDevs = devices.filter((d) => d.kind === 'audioinput');

      setVideoDevices(vDevs);
      setAudioDevices(aDevs);

      if (vDevs.length > 0 && !selectedVideoDeviceId) {
        setSelectedVideoDeviceId(vDevs[0].deviceId);
      }
      if (aDevs.length > 0 && !selectedAudioDeviceId) {
        setSelectedAudioDeviceId(aDevs[0].deviceId);
      }
    } catch (err) {
      console.warn('Error enumerating capture devices:', err);
    }
  }, [selectedVideoDeviceId, selectedAudioDeviceId]);

  useEffect(() => {
    if (pageVisible) {
      refreshDevices();
    }
  }, [pageVisible, refreshDevices]);

  // Helper to draw a video element inside a box on the canvas
  const drawVideoInBox = (
    ctx,
    vid,
    boxX,
    boxY,
    boxW,
    boxH,
    { fit = 'contain', mirror = false, borderRadius = 0, borderColor = null } = {}
  ) => {
    if (!vid || vid.readyState < 2) return;
    const vW = vid.videoWidth || 1280;
    const vH = vid.videoHeight || 720;

    ctx.save();
    if (borderRadius > 0) {
      ctx.beginPath();
      if (ctx.roundRect) {
        ctx.roundRect(boxX, boxY, boxW, boxH, borderRadius);
      } else {
        ctx.rect(boxX, boxY, boxW, boxH);
      }
      ctx.clip();
    } else {
      ctx.beginPath();
      ctx.rect(boxX, boxY, boxW, boxH);
      ctx.clip();
    }

    ctx.fillStyle = '#0f172a';
    ctx.fillRect(boxX, boxY, boxW, boxH);

    const scale =
      fit === 'cover'
        ? Math.max(boxW / vW, boxH / vH)
        : Math.min(boxW / vW, boxH / vH);
    const drawW = vW * scale;
    const drawH = vH * scale;
    const offsetX = boxX + (boxW - drawW) / 2;
    const offsetY = boxY + (boxH - drawH) / 2;

    if (mirror) {
      ctx.translate(boxX + boxW / 2, boxY + boxH / 2);
      ctx.scale(-1, 1);
      ctx.translate(-(boxX + boxW / 2), -(boxY + boxH / 2));
    }

    ctx.drawImage(vid, offsetX, offsetY, drawW, drawH);
    ctx.restore();

    if (borderColor) {
      ctx.save();
      ctx.strokeStyle = borderColor;
      ctx.lineWidth = 3;
      ctx.beginPath();
      if (borderRadius > 0 && ctx.roundRect) {
        ctx.roundRect(boxX, boxY, boxW, boxH, borderRadius);
      } else {
        ctx.rect(boxX, boxY, boxW, boxH);
      }
      ctx.stroke();
      ctx.restore();
    }
  };

  // Multi-Layout Canvas Compositor (1280x720 @ 30fps) - immune to background tab throttling via Web Worker clock
  const startCanvasCompositor = useCallback(() => {
    const canvas = previewCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (fallbackIntervalRef.current) {
      clearInterval(fallbackIntervalRef.current);
      fallbackIntervalRef.current = null;
    }
    if (workerTickerRef.current) {
      try {
        workerTickerRef.current.postMessage('stop');
        workerTickerRef.current.terminate();
      } catch (e) {}
      workerTickerRef.current = null;
    }

    const renderFrame = () => {
      const now = performance.now();
      // Cap at ~30fps to avoid redundant double-draws
      if (now - lastRenderTimeRef.current < 28) return;
      lastRenderTimeRef.current = now;

      const width = canvas.width;
      const height = canvas.height;
      const activeLayout = layoutPresetRef.current || 'pip_br';
      const scaleMult = PIP_SIZE_SCALE[pipSizeRef.current] || 1.0;
      const mirrorCam = Boolean(mirrorWebcamRef.current);
      const showBanner = Boolean(showLowerThirdRef.current);

      const bgGrad = ctx.createLinearGradient(0, 0, width, height);
      bgGrad.addColorStop(0, '#0f172a');
      bgGrad.addColorStop(1, '#1e1b4b');
      ctx.fillStyle = bgGrad;
      ctx.fillRect(0, 0, width, height);

      const screenVid = screenVideoRef.current;
      const webcamVid = webcamVideoRef.current;

      const hasScreen =
        screenVid &&
        screenVid.readyState >= 2 &&
        screenStreamRef.current?.active &&
        activeLayout !== 'webcam_only';

      const hasWebcam =
        isWebcamEnabledRef.current &&
        webcamVid &&
        webcamVid.readyState >= 2 &&
        webcamStreamRef.current?.active &&
        activeLayout !== 'screen_only';

      if (activeLayout === 'screen_only' || (hasScreen && !hasWebcam)) {
        drawVideoInBox(ctx, screenVid, 0, 0, width, height, { fit: 'contain' });
      } else if (activeLayout === 'webcam_only' || (hasWebcam && !hasScreen)) {
        drawVideoInBox(ctx, webcamVid, 0, 0, width, height, {
          fit: 'contain',
          mirror: mirrorCam
        });
      } else if (hasScreen && hasWebcam) {
        if (activeLayout === 'split_50_50') {
          const pad = 16;
          const gap = 14;
          const colW = (width - pad * 2 - gap) / 2;
          const colH = height - pad * 2;
          drawVideoInBox(ctx, screenVid, pad, pad, colW, colH, {
            fit: 'contain',
            borderRadius: 12,
            borderColor: '#334155'
          });
          drawVideoInBox(ctx, webcamVid, pad + colW + gap, pad, colW, colH, {
            fit: 'cover',
            mirror: mirrorCam,
            borderRadius: 12,
            borderColor: '#8b5cf6'
          });
        } else if (activeLayout === 'split_70_30') {
          const pad = 16;
          const gap = 14;
          const leftW = Math.round((width - pad * 2 - gap) * 0.7);
          const rightW = width - pad * 2 - gap - leftW;
          const boxH = height - pad * 2;

          drawVideoInBox(ctx, screenVid, pad, pad, leftW, boxH, {
            fit: 'contain',
            borderRadius: 12,
            borderColor: '#334155'
          });

          const camH = Math.round(rightW * 0.75);
          const camY = pad + Math.round((boxH - camH) / 2);
          drawVideoInBox(ctx, webcamVid, pad + leftW + gap, camY, rightW, camH, {
            fit: 'cover',
            mirror: mirrorCam,
            borderRadius: 14,
            borderColor: '#8b5cf6'
          });
        } else if (activeLayout === 'webcam_pip') {
          drawVideoInBox(ctx, webcamVid, 0, 0, width, height, {
            fit: 'cover',
            mirror: mirrorCam
          });
          const pipW = Math.round(320 * scaleMult);
          const pipH = Math.round(180 * scaleMult);
          const pipX = width - pipW - 24;
          const pipY = height - pipH - (showBanner ? 74 : 24);
          drawVideoInBox(ctx, screenVid, pipX, pipY, pipW, pipH, {
            fit: 'contain',
            borderRadius: 10,
            borderColor: '#38bdf8'
          });
        } else if (activeLayout === 'circle_br') {
          drawVideoInBox(ctx, screenVid, 0, 0, width, height, { fit: 'contain' });
          const diameter = Math.round(190 * scaleMult);
          const radius = diameter / 2;
          const centerX = width - radius - 28;
          const centerY = height - radius - (showBanner ? 76 : 28);

          ctx.save();
          ctx.beginPath();
          ctx.arc(centerX, centerY, radius + 3, 0, Math.PI * 2);
          ctx.fillStyle = '#8b5cf6';
          ctx.fill();

          ctx.beginPath();
          ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
          ctx.clip();

          const vW = webcamVid.videoWidth || 1280;
          const vH = webcamVid.videoHeight || 720;
          const s = Math.max(diameter / vW, diameter / vH);
          const dW = vW * s;
          const dH = vH * s;
          const oX = centerX - dW / 2;
          const oY = centerY - dH / 2;

          if (mirrorCam) {
            ctx.translate(centerX, centerY);
            ctx.scale(-1, 1);
            ctx.translate(-centerX, -centerY);
          }
          ctx.drawImage(webcamVid, oX, oY, dW, dH);
          ctx.restore();
        } else {
          drawVideoInBox(ctx, screenVid, 0, 0, width, height, { fit: 'contain' });
          const pipW = Math.round(280 * scaleMult);
          const pipH = Math.round(158 * scaleMult);
          const margin = 24;
          const bottomMargin = showBanner ? 74 : 24;

          let pipX = width - pipW - margin;
          let pipY = height - pipH - bottomMargin;
          if (activeLayout === 'pip_bl') {
            pipX = margin;
            pipY = height - pipH - bottomMargin;
          } else if (activeLayout === 'pip_tr') {
            pipX = width - pipW - margin;
            pipY = margin;
          } else if (activeLayout === 'pip_tl') {
            pipX = margin;
            pipY = margin;
          }

          drawVideoInBox(ctx, webcamVid, pipX, pipY, pipW, pipH, {
            fit: 'cover',
            mirror: mirrorCam,
            borderRadius: 10,
            borderColor: '#8b5cf6'
          });
        }
      }

      // Lower-Third Title Banner
      if ((hasScreen || hasWebcam) && showBanner && bannerTextRef.current.title) {
        const bHeight = 44;
        const bY = height - bHeight - 12;
        const bX = 20;
        const bWidth = Math.min(width - 40, 620);

        ctx.save();
        ctx.fillStyle = 'rgba(15, 23, 42, 0.82)';
        ctx.strokeStyle = 'rgba(139, 92, 246, 0.7)';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        if (ctx.roundRect) {
          ctx.roundRect(bX, bY, bWidth, bHeight, 8);
        } else {
          ctx.rect(bX, bY, bWidth, bHeight);
        }
        ctx.fill();
        ctx.stroke();

        ctx.fillStyle = '#8b5cf6';
        ctx.fillRect(bX, bY, 6, bHeight);

        ctx.fillStyle = '#f8fafc';
        ctx.font = 'bold 15px sans-serif';
        ctx.textAlign = 'left';
        ctx.fillText(
          bannerTextRef.current.title.slice(0, 65),
          bX + 16,
          bannerTextRef.current.subtitle ? bY + 19 : bY + 27
        );

        if (bannerTextRef.current.subtitle) {
          ctx.fillStyle = '#cbd5e1';
          ctx.font = '12px sans-serif';
          ctx.fillText(bannerTextRef.current.subtitle.slice(0, 80), bX + 16, bY + 36);
        }
        ctx.restore();
      }

      if (!hasScreen && !hasWebcam) {
        ctx.fillStyle = '#94a3b8';
        ctx.font = '600 18px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(
          'Select a Layout & Capture Devices, then click "Preview Sources" or "Start Live & Record"',
          width / 2,
          height / 2
        );
      }

      // Subtle 1x1 subpixel pulse in bottom right so canvas.captureStream() always registers active motion even on completely static screen shares
      ctx.fillStyle = Math.floor(Date.now() / 1000) % 2 === 0 ? '#0f172a' : '#0f172b';
      ctx.fillRect(width - 1, height - 1, 1, 1);

      // Force canvas stream track to capture frame if requestFrame is supported
      if (canvasStreamRef.current) {
        const vTrack = canvasStreamRef.current.getVideoTracks()[0];
        if (vTrack && typeof vTrack.requestFrame === 'function') {
          vTrack.requestFrame();
        }
      }
    };

    // 1. Web Worker ticker runs in separate background thread — NEVER throttled when tab is backgrounded, minimized, or covered!
    try {
      const workerCode = `
        let timer = null;
        self.onmessage = function(e) {
          if (e.data === 'start') {
            if (timer) clearInterval(timer);
            timer = setInterval(function() {
              self.postMessage('tick');
            }, 33);
          } else if (e.data === 'stop') {
            if (timer) clearInterval(timer);
            timer = null;
          }
        };
      `;
      const workerBlob = new Blob([workerCode], { type: 'application/javascript' });
      const workerUrl = URL.createObjectURL(workerBlob);
      const worker = new Worker(workerUrl);
      worker.onmessage = () => {
        renderFrame();
      };
      worker.postMessage('start');
      workerTickerRef.current = worker;
    } catch (wErr) {
      console.warn('Web Worker ticker not supported, relying on fallback timer:', wErr);
    }

    // 2. High-precision fallback interval in main thread
    fallbackIntervalRef.current = setInterval(() => {
      renderFrame();
    }, 33);

    // 3. requestAnimationFrame loop for ultra-smooth 60Hz display when tab is in foreground
    const rAfLoop = () => {
      renderFrame();
      animationFrameRef.current = requestAnimationFrame(rAfLoop);
    };
    animationFrameRef.current = requestAnimationFrame(rAfLoop);
  }, []);

  useEffect(() => {
    if (pageVisible) {
      const t = setTimeout(() => {
        startCanvasCompositor();
      }, 150);
      return () => clearTimeout(t);
    }
  }, [pageVisible, startCanvasCompositor]);

  // Switch Broadcast Scene Layout ONLY (never modifies device or capture settings; works live mid-broadcast)
  const handleSelectLayoutPreset = (preset) => {
    setLayoutPreset(preset.id);
    layoutPresetRef.current = preset.id;
  };

  // Persistent Web Audio API mixer so switching Mic or Window Capture mid-broadcast never interrupts MediaRecorder
  const ensureAudioMixer = useCallback(() => {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
      const audioCtx = new AudioCtx();
      audioContextRef.current = audioCtx;
      const destination = audioCtx.createMediaStreamDestination();
      mixedAudioDestRef.current = destination;

      // Silent keepalive oscillator so MediaRecorder always has a continuous audio track
      const oscillator = audioCtx.createOscillator();
      const gainNode = audioCtx.createGain();
      gainNode.gain.value = 0;
      oscillator.connect(gainNode);
      gainNode.connect(destination);
      oscillator.start();
    } else if (audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume().catch(() => {});
    }
    return {
      audioCtx: audioContextRef.current,
      destination: mixedAudioDestRef.current
    };
  }, []);

  // Clean up all media tracks
  const cleanupMediaStreams = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (fallbackIntervalRef.current) {
      clearInterval(fallbackIntervalRef.current);
      fallbackIntervalRef.current = null;
    }
    if (workerTickerRef.current) {
      try {
        workerTickerRef.current.postMessage('stop');
        workerTickerRef.current.terminate();
      } catch (e) {}
      workerTickerRef.current = null;
    }
    canvasStreamRef.current = null;
    if (webcamStreamRef.current) {
      webcamStreamRef.current.getTracks().forEach((t) => t.stop());
      webcamStreamRef.current = null;
    }
    if (screenStreamRef.current) {
      screenStreamRef.current.getTracks().forEach((t) => t.stop());
      screenStreamRef.current = null;
    }
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach((t) => t.stop());
      micStreamRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    setIsWebcamActive(false);
    setIsWindowActive(false);
    setWindowLabel('');
    setIsPreviewActive(false);
  }, []);

  // 1A. Start or switch Webcam Capture Device (can be called before or mid-broadcast)
  const acquireWebcamCapture = async (deviceIdOverride) => {
    const targetDeviceId =
      deviceIdOverride !== undefined ? deviceIdOverride : selectedVideoDeviceId;

    if (webcamStreamRef.current) {
      webcamStreamRef.current.getTracks().forEach((t) => t.stop());
      webcamStreamRef.current = null;
    }

    if (targetDeviceId === 'none') {
      setIsWebcamActive(false);
      return false;
    }

    try {
      const videoConstraints = targetDeviceId
        ? {
            deviceId: { exact: targetDeviceId },
            width: { ideal: 1280 },
            height: { ideal: 720 },
            frameRate: { ideal: 30 }
          }
        : { width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30 } };

      const camStream = await navigator.mediaDevices.getUserMedia({
        video: videoConstraints,
        audio: false
      });
      webcamStreamRef.current = camStream;
      if (webcamVideoRef.current) {
        webcamVideoRef.current.srcObject = camStream;
        await webcamVideoRef.current.play().catch(() => {});
      }
      ensureAudioMixer();
      setIsWebcamActive(true);
      setIsWebcamEnabled(true);
      isWebcamEnabledRef.current = true;
      setIsPreviewActive(true);
      startCanvasCompositor();
      return true;
    } catch (err) {
      console.warn('Webcam capture error:', err);
      setIsWebcamActive(false);
      return false;
    }
  };

  // 1B. Start or switch Window Capture (can be called before or mid-broadcast)
  const acquireWindowCapture = async () => {
    try {
      const displayStream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          width: { ideal: 1920 },
          height: { ideal: 1080 },
          frameRate: { ideal: 30 }
        },
        audio: includeSystemAudio
      });

      if (screenStreamRef.current) {
        screenStreamRef.current.getTracks().forEach((t) => t.stop());
        screenStreamRef.current = null;
      }

      screenStreamRef.current = displayStream;
      if (screenVideoRef.current) {
        screenVideoRef.current.srcObject = displayStream;
        await screenVideoRef.current.play().catch(() => {});
      }

      const videoTrack = displayStream.getVideoTracks()[0];
      setIsWindowActive(true);
      setWindowLabel(videoTrack?.label || 'Window / Screen Active');

      if (videoTrack) {
        videoTrack.onended = () => {
          setIsWindowActive(false);
          setWindowLabel('');
        };
      }

      // Always ensure persistent audio mixer is initialized and connect Window / System Audio if present
      const { audioCtx, destination } = ensureAudioMixer();
      if (displayStream.getAudioTracks().length > 0) {
        const sysSource = audioCtx.createMediaStreamSource(displayStream);
        sysSource.connect(destination);
      }

      setIsPreviewActive(true);
      startCanvasCompositor();
      return true;
    } catch (err) {
      if (err.name !== 'NotAllowedError' && err.name !== 'AbortError') {
        message.error(err.message || 'Unable to start Window Capture.');
      }
      return false;
    }
  };

  // 1C. Start or switch Audio Capture Device (Microphone) (can be called before or mid-broadcast)
  const acquireAudioCapture = async (deviceIdOverride) => {
    const targetAudioId =
      deviceIdOverride !== undefined ? deviceIdOverride : selectedAudioDeviceId;

    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach((t) => t.stop());
      micStreamRef.current = null;
    }

    if (targetAudioId === 'none') {
      return false;
    }

    try {
      const audioConstraints = targetAudioId
        ? {
            deviceId: { exact: targetAudioId },
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true
          }
        : {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true
          };

      const micStream = await navigator.mediaDevices.getUserMedia({
        video: false,
        audio: audioConstraints
      });
      micStream.getAudioTracks().forEach((track) => {
        track.enabled = !isMicMuted;
      });
      micStreamRef.current = micStream;

      const { audioCtx, destination } = ensureAudioMixer();
      if (micStream.getAudioTracks().length > 0) {
        const micSource = audioCtx.createMediaStreamSource(micStream);
        micSource.connect(destination);
      }
      return true;
    } catch (micErr) {
      console.warn('Microphone capture unavailable:', micErr);
      return false;
    }
  };

  // Acquire active sources for Preview / Start Broadcast
  const acquireStreams = async () => {
    try {
      ensureAudioMixer();

      // 1. Acquire Webcam if not already active (and not set to 'none')
      if (!webcamStreamRef.current?.active && selectedVideoDeviceId !== 'none') {
        await acquireWebcamCapture(selectedVideoDeviceId);
      }

      // 2. Acquire Microphone if not already active (and not set to 'none')
      if (!micStreamRef.current?.active && selectedAudioDeviceId !== 'none') {
        await acquireAudioCapture(selectedAudioDeviceId);
      }

      // 3. Acquire Window Capture if not already active (or when user clicks Preview / Select Window)
      if (!screenStreamRef.current?.active && layoutPresetRef.current !== 'webcam_only') {
        await acquireWindowCapture();
      }

      const hasAnyVideo =
        Boolean(webcamStreamRef.current?.active) ||
        Boolean(screenStreamRef.current?.active);
      if (hasAnyVideo) {
        setIsPreviewActive(true);
        startCanvasCompositor();
        return true;
      }

      message.warning('Please select a Webcam or Window Capture source to begin.');
      return false;
    } catch (err) {
      console.error('Error acquiring media sources:', err);
      message.error(err.message || 'Unable to access selected camera/window or microphone.');
      return false;
    }
  };

  // Select local directory or file save path on local system
  const handleSelectLocalPath = async () => {
    try {
      if (window.showDirectoryPicker) {
        const dirHandle = await window.showDirectoryPicker({ mode: 'readwrite' });
        localDirHandleRef.current = dirHandle;
        localFileHandleRef.current = null;
        setLocalDirName(dirHandle.name);
        message.success(`Local recording folder selected: ${dirHandle.name}`);
        return;
      }

      if (window.showSaveFilePicker) {
        const ext = localRecordingFormat === 'flv' ? 'flv' : 'mkv';
        const baseSuggested = (
          localCustomFileName || `session_${activeSession?.rtmp_session_id || 'live'}.${ext}`
        ).replace(/\.(webm|mkv|flv)$/i, '');
        const fileHandle = await window.showSaveFilePicker({
          suggestedName: `${baseSuggested}.${ext}`,
          types: [
            {
              description: 'Matroska Video File (.mkv)',
              accept: { 'video/x-matroska': ['.mkv'] }
            },
            {
              description: 'Flash Video File (.flv)',
              accept: { 'video/x-flv': ['.flv'] }
            }
          ]
        });
        localFileHandleRef.current = fileHandle;
        localDirHandleRef.current = null;
        setLocalDirName(fileHandle.name);
        setLocalCustomFileName(fileHandle.name);
        if (fileHandle.name.toLowerCase().endsWith('.flv')) {
          setLocalRecordingFormat('flv');
        } else if (fileHandle.name.toLowerCase().endsWith('.mkv')) {
          setLocalRecordingFormat('mkv');
        }
        message.success(`Local file path selected: ${fileHandle.name}`);
        return;
      }

      message.info(
        `Your browser will save the .${localRecordingFormat} recording directly to your local Downloads folder using the filename below.`
      );
    } catch (err) {
      if (err.name !== 'AbortError') {
        message.warning(err.message || 'Could not open local folder picker.');
      }
    }
  };

  // Initialize local system file writer (.mkv or .flv)
  const initLocalFileWriter = async (sessionObj) => {
    localWritableRef.current = null;
    localChunksRef.current = [];
    setLocalBytesWritten(0);
    setSavedLocalFilePath('');

    if (!enableLocalRecording) return;

    const ext = localRecordingFormat === 'flv' ? 'flv' : 'mkv';
    const sId = sessionObj?.rtmp_session_id || sessionObj?.session_id || Date.now();
    const rawName = (localCustomFileName || `session_${sId}.${ext}`).replace(
      /\.(webm|mkv|flv)$/i,
      ''
    );
    const fileName = `${rawName}.${ext}`;

    try {
      if (localFileHandleRef.current) {
        const writable = await localFileHandleRef.current.createWritable();
        localWritableRef.current = writable;
        setSavedLocalFilePath(localFileHandleRef.current.name);
        return;
      }

      if (localDirHandleRef.current) {
        let targetDirHandle = localDirHandleRef.current;
        const cleanSub = localSubfolderPath.trim().replace(/^[/\\]+|[/\\]+$/g, '');
        if (cleanSub) {
          const parts = cleanSub.split(/[/\\]+/).filter(Boolean);
          for (const part of parts) {
            targetDirHandle = await targetDirHandle.getDirectoryHandle(part, {
              create: true
            });
          }
        }
        const fileHandle = await targetDirHandle.getFileHandle(fileName, {
          create: true
        });
        const writable = await fileHandle.createWritable();
        localWritableRef.current = writable;
        const fullDisplayPath = cleanSub
          ? `${localDirHandleRef.current.name}/${cleanSub}/${fileName}`
          : `${localDirHandleRef.current.name}/${fileName}`;
        setSavedLocalFilePath(fullDisplayPath);
      }
    } catch (err) {
      console.warn('Direct local file stream unavailable, using Blob download fallback:', err);
      localWritableRef.current = null;
    }
  };

  // Start Live Broadcast & Recording
  const handleStartBroadcast = async () => {
    let targetSession = activeSessionRef.current || activeSession || session;

    if (!targetSession?.rtmp_session_id && !targetSession?.session_id) {
      const cId = propCourseId || selectedCourseId;
      const mId = propModuleId || selectedModuleId;

      if (!cId || !mId) {
        message.warning('Please select a Course and Module for this session.');
        return;
      }
      if (!quickTitle || !quickTitle.trim()) {
        message.warning('Please enter a Session Title.');
        return;
      }

      try {
        const now = new Date();
        const oneHourLater = new Date(now.getTime() + 60 * 60 * 1000);
        const createRes = await scheduleRTMPSession({
          courses_id: Number(cId),
          modules_id: Number(mId),
          vid_cat_id: Number(quickSlNo) || 1,
          video_title: quickTitle.trim(),
          instructor_id: quickInstructorId || null,
          rtmps_schedul_start_time: now.toISOString(),
          rtmps_schedul_end_time: oneHourLater.toISOString(),
          is_web_broadcast: true
        });

        if (!createRes?.success || (!createRes.rtmp_session_id && !createRes.session_id)) {
          throw new Error(createRes?.message || 'Failed to schedule web session.');
        }

        targetSession = {
          rtmp_session_id: createRes.rtmp_session_id || createRes.session_id,
          session_id: createRes.session_id || createRes.rtmp_session_id,
          video_id: createRes.video_id,
          video_title: quickTitle.trim(),
          courses_id: Number(cId),
          modules_id: Number(mId),
          rtmp_stream_key: createRes.rtmp_stream_key,
          rtmp_url: createRes.rtmp_url,
          rtmp_status: 'SCHEDULED'
        };
        setActiveSession(targetSession);
        activeSessionRef.current = targetSession;
        if (onSessionUpdated) onSessionUpdated();
      } catch (createErr) {
        message.error(createErr.message || 'Could not create session for web broadcast.');
        return;
      }
    }

    // Always ensure audio mixer and microphone are initialized even if preview was started via webcam/window button
    ensureAudioMixer();
    if (!micStreamRef.current?.active && selectedAudioDeviceId !== 'none') {
      await acquireAudioCapture(selectedAudioDeviceId);
    }

    let ready = isPreviewActive;
    if (!ready) {
      ready = await acquireStreams();
    }
    if (!ready || !previewCanvasRef.current) {
      return;
    }

    try {
      await initLocalFileWriter(targetSession);

      const canvasStream = previewCanvasRef.current.captureStream(30);
      canvasStreamRef.current = canvasStream;
      const compositeStream = new MediaStream();

      canvasStream.getVideoTracks().forEach((t) => compositeStream.addTrack(t));
      if (mixedAudioDestRef.current?.stream) {
        mixedAudioDestRef.current.stream
          .getAudioTracks()
          .forEach((t) => compositeStream.addTrack(t));
      }

      const mimeTypes = [
        'video/x-matroska;codecs=avc1,opus',
        'video/webm;codecs=h264,opus',
        'video/webm;codecs=h264',
        'video/webm;codecs=vp8,opus',
        'video/webm;codecs=vp9,opus',
        'video/webm'
      ];
      const supportedMimeType =
        mimeTypes.find((type) => MediaRecorder.isTypeSupported(type)) || '';

      const vBps =
        VIDEO_BITRATE_OPTIONS.find((o) => o.value === videoBitrate)?.bps || 1500000;
      const aBps =
        AUDIO_BITRATE_OPTIONS.find((o) => o.value === audioBitrate)?.bps || 128000;

      const recorderOptions = {
        videoBitsPerSecond: vBps,
        audioBitsPerSecond: aBps
      };
      if (supportedMimeType) {
        recorderOptions.mimeType = supportedMimeType;
      }

      const recorder = new MediaRecorder(compositeStream, recorderOptions);
      mediaRecorderRef.current = recorder;
      chunkSeqRef.current = 0;
      uploadChainRef.current = Promise.resolve();
      setChunksSent(0);
      setServerBytesSent(0);
      setElapsedSeconds(0);

      const sessionIdForUpload =
        targetSession.rtmp_session_id || targetSession.session_id;

      recorder.ondataavailable = async (event) => {
        if (!event.data || event.data.size === 0) return;
        const chunkBlob = event.data;
        const currentSeq = chunkSeqRef.current++;

        if (enableLocalRecording) {
          setLocalBytesWritten((prev) => prev + chunkBlob.size);
          localChunksRef.current.push(chunkBlob);
        }

        uploadChainRef.current = uploadChainRef.current
          .then(async () => {
            await sendWebBroadcastChunk(sessionIdForUpload, chunkBlob, {
              videoBitrate,
              audioBitrate,
              isFirstChunk: currentSeq === 0
            });
            setChunksSent((prev) => prev + 1);
            setServerBytesSent((prev) => prev + chunkBlob.size);
            if (currentSeq === 1 && onSessionUpdated) {
              onSessionUpdated();
            }
          })
          .catch((uploadErr) => {
            console.error(`Failed uploading web broadcast chunk #${currentSeq}:`, uploadErr);
          });
      };

      recorder.start(1000);
      setIsBroadcasting(true);
      isBroadcastingRef.current = true;

      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = setInterval(() => {
        setElapsedSeconds((prev) => prev + 1);
      }, 1000);

      message.success(
        `Live Web Broadcast started (${videoBitrate} video / ${audioBitrate} audio)!`
      );
    } catch (err) {
      console.error('Error starting MediaRecorder:', err);
      message.error(err.message || 'Could not initialize browser media recorder.');
    }
  };

  // Finalize local system recording in genuine .mkv or .flv format
  const finalizeLocalRecording = async (sessionObj) => {
    if (!enableLocalRecording) return;

    const ext = localRecordingFormat === 'flv' ? 'flv' : 'mkv';
    const mimeType = ext === 'flv' ? 'video/x-flv' : 'video/x-matroska';
    const sId = sessionObj?.rtmp_session_id || sessionObj?.session_id || Date.now();
    const rawName = (localCustomFileName || `session_${sId}.${ext}`).replace(
      /\.(webm|mkv|flv)$/i,
      ''
    );
    const fileName = `${rawName}.${ext}`;

    try {
      let recordingBlob = null;
      if (sessionObj?.rtmp_session_id || sessionObj?.session_id) {
        try {
          recordingBlob = await fetchWebBroadcastLocalFile(
            sessionObj.rtmp_session_id || sessionObj.session_id,
            ext
          );
        } catch (fetchErr) {
          console.warn('Could not fetch server MKV/FLV file, using local buffer fallback:', fetchErr);
        }
      }

      if ((!recordingBlob || recordingBlob.size === 0) && localChunksRef.current.length > 0) {
        recordingBlob = new Blob(localChunksRef.current, { type: mimeType });
      }

      if (localWritableRef.current) {
        if (recordingBlob && recordingBlob.size > 0) {
          await localWritableRef.current.write(recordingBlob);
        }
        await localWritableRef.current.close();
        localWritableRef.current = null;
        message.success(
          `Local ${ext.toUpperCase()} recording saved to disk: ${savedLocalFilePath || fileName}`
        );
        return;
      }

      if (recordingBlob && recordingBlob.size > 0) {
        const url = URL.createObjectURL(recordingBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 10000);
        setSavedLocalFilePath(`Downloads/${fileName}`);
        message.success(`Local ${ext.toUpperCase()} recording downloaded: ${fileName}`);
      }
    } catch (err) {
      console.error('Error finalizing local recording:', err);
      message.warning(err.message || 'Failed to finalize local recording file.');
    }
  };

  // Stop Live Broadcast, save local MKV/FLV recording, and trigger server MP4 transcoding
  const handleStopBroadcast = async () => {
    if (isStopping) return;
    setIsStopping(true);

    const targetSession = activeSessionRef.current || activeSession || session;
    const sessionId = targetSession?.rtmp_session_id || targetSession?.session_id;

    try {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }

      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state !== 'inactive'
      ) {
        await new Promise((resolve) => {
          const rec = mediaRecorderRef.current;
          const prevOnStop = rec.onstop;
          rec.onstop = (e) => {
            if (prevOnStop) prevOnStop(e);
            resolve();
          };
          rec.stop();
        });
      }

      setIsBroadcasting(false);
      isBroadcastingRef.current = false;

      await uploadChainRef.current.catch(() => {});

      if (sessionId) {
        // Stop FFmpeg first so .mkv and .flv files are cleanly flushed on server
        await stopWebBroadcast(sessionId).catch(() => {});
      }

      await finalizeLocalRecording(targetSession);
      cleanupMediaStreams();

      if (sessionId) {
        await updateRTMPSessionStatus(sessionId, 'COMPLETED');
        message.success(
          'Session completed! Server is transcoding the recording to MP4 and publishing to Module Videos.'
        );
        if (onSessionUpdated) onSessionUpdated();
      }

      setIsStopping(false);
      onClose();
    } catch (err) {
      console.error('Error stopping web broadcast:', err);
      message.error(err.message || 'An error occurred while stopping the session.');
      setIsStopping(false);
    }
  };

  const handleToggleMic = () => {
    const nextMuted = !isMicMuted;
    setIsMicMuted(nextMuted);
    if (micStreamRef.current) {
      micStreamRef.current.getAudioTracks().forEach((track) => {
        track.enabled = !nextMuted;
      });
    }
  };

  const handleToggleWebcam = () => {
    const nextEnabled = !isWebcamEnabled;
    setIsWebcamEnabled(nextEnabled);
    isWebcamEnabledRef.current = nextEnabled;
    if (webcamStreamRef.current) {
      webcamStreamRef.current.getVideoTracks().forEach((track) => {
        track.enabled = nextEnabled;
      });
    }
  };

  const handleBackClose = () => {
    if (isBroadcasting) {
      message.warning('Please stop the live broadcast before leaving the studio page.');
      return;
    }
    if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    if (chatPollIntervalRef.current) clearInterval(chatPollIntervalRef.current);
    cleanupMediaStreams();
    onClose();
  };

  useEffect(() => {
    return () => {
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
      if (chatPollIntervalRef.current) clearInterval(chatPollIntervalRef.current);
      cleanupMediaStreams();
    };
  }, [cleanupMediaStreams]);

  if (!pageVisible) return null;

  const formatDuration = (secs) => {
    const hrs = Math.floor(secs / 3600);
    const mins = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    return [
      hrs > 0 ? String(hrs).padStart(2, '0') : null,
      String(mins).padStart(2, '0'),
      String(s).padStart(2, '0')
    ]
      .filter(Boolean)
      .join(':');
  };

  const formatBytes = (bytes) => {
    if (!bytes || bytes <= 0) return '0 MB';
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const displaySession = activeSession || session;
  const attendingViewers = activeViewers.filter((v) => {
    const r = (v.role || '').toUpperCase();
    const vId = String(v.login_id || v.session_viewers_id || v.id);
    return r !== 'ADMIN' && r !== 'STUDIO' && !hiddenParticipantIds.includes(vId);
  });

  return (
    <div
      style={{
        padding: 24,
        background: 'var(--panel-color)',
        minHeight: '85vh',
        color: 'var(--text-color)',
        border: '1px solid var(--border-color)',
        borderRadius: 12
      }}
    >
      <style>{`
        @keyframes livePulse {
          0% { box-shadow: 0 0 0 0 rgba(16,185,129,0.6); }
          70% { box-shadow: 0 0 0 7px rgba(16,185,129,0); }
          100% { box-shadow: 0 0 0 0 rgba(16,185,129,0); }
        }
      `}</style>

      {/* Hidden video elements feeding the HTML5 Canvas compositor */}
      <video ref={screenVideoRef} playsInline muted style={{ display: 'none' }} />
      <video ref={webcamVideoRef} playsInline muted style={{ display: 'none' }} />

      {/* Top Page Navigation & Broadcast Action Bar */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 20,
          flexWrap: 'wrap',
          gap: 12
        }}
      >
        <Space wrap size={12}>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={handleBackClose}
            disabled={isBroadcasting || isStopping}
            style={{ borderRadius: 6 }}
          >
            Back
          </Button>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <h2 style={{ color: 'var(--text-color)', margin: 0, fontSize: 20 }}>
                Start From Web — Live Classroom Studio & Recorder
              </h2>
              {isBroadcasting && (
                <Tag color="red" style={{ fontWeight: 700, padding: '4px 12px', borderRadius: 999, margin: 0 }}>
                  ● LIVE & RECORDING ({formatDuration(elapsedSeconds)})
                </Tag>
              )}
              {!isBroadcasting && isPreviewActive && (
                <Tag color="processing" style={{ fontWeight: 600, padding: '4px 10px', borderRadius: 999, margin: 0 }}>
                  PREVIEW READY
                </Tag>
              )}
              <Badge
                count={`${attendingViewers.length} Watching`}
                style={{ backgroundColor: '#52c41a' }}
              />
            </div>
            <div style={{ marginTop: 4, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Tag color="blue" style={{ margin: 0, borderRadius: 6 }}>
                Course: {coursesList.find((c) => Number(c.courses_id) === Number(selectedCourseId))?.courses_name || course?.courses_name || courseName || displaySession?.courses_name || 'Select Below'}
              </Tag>
              <Tag color="purple" style={{ margin: 0, borderRadius: 6 }}>
                Module: {modulesList.find((m) => Number(m.modules_id) === Number(selectedModuleId))?.modules_name || module?.modules_name || moduleName || displaySession?.modules_name || 'Select Below'}
              </Tag>
              {displaySession?.video_title && (
                <Tag color="cyan" style={{ margin: 0, borderRadius: 6 }}>
                  Session: {displaySession.video_title} (#{displaySession.rtmp_session_id || displaySession.session_id})
                </Tag>
              )}
            </div>
          </div>
        </Space>

        <Space wrap>
          {!isBroadcasting ? (
            <>
              <Button
                icon={<DesktopOutlined />}
                onClick={acquireStreams}
                disabled={isStopping}
                style={{ borderRadius: 6 }}
              >
                {isPreviewActive ? 'Change Capture Sources' : 'Preview Sources'}
              </Button>
              <Button
                type="primary"
                size="large"
                icon={<PlayCircleOutlined />}
                onClick={handleStartBroadcast}
                loading={isStopping}
                style={{
                  background: '#8b5cf6',
                  borderColor: '#8b5cf6',
                  fontWeight: 600,
                  borderRadius: 6
                }}
              >
                Start Live & Record
              </Button>
            </>
          ) : (
            <Button
              type="primary"
              size="large"
              danger
              icon={<StopOutlined />}
              onClick={handleStopBroadcast}
              loading={isStopping}
              style={{ fontWeight: 600, borderRadius: 6 }}
            >
              Stop Broadcast & Save Recording
            </Button>
          )}
        </Space>
      </div>

      {/* Quick Session Creation Bar when opened directly from a top-level "Start From Web" button */}
      {!displaySession && (
        <Card
          size="small"
          style={{
            marginBottom: 16,
            borderRadius: 10,
            border: '1px solid rgba(139, 92, 246, 0.35)',
            background: 'rgba(139, 92, 246, 0.05)'
          }}
        >
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10, color: '#7c3aed' }}>
            Session Details (Automatically schedules & records like an RTMP session)
          </div>
          <Row gutter={[12, 12]}>
            {!propCourseId && (
              <Col xs={24} md={6}>
                <div style={{ fontSize: 12, marginBottom: 4, fontWeight: 500 }}>Course *</div>
                <Select
                  style={{ width: '100%' }}
                  placeholder="Select Course"
                  value={selectedCourseId || undefined}
                  onChange={(val) => {
                    setSelectedCourseId(val);
                    setSelectedModuleId('');
                  }}
                  disabled={isBroadcasting || isStopping}
                  showSearch
                  optionFilterProp="children"
                >
                  {coursesList.map((c) => (
                    <Option key={c.courses_id} value={c.courses_id}>
                      {c.courses_name}
                    </Option>
                  ))}
                </Select>
              </Col>
            )}
            {!propModuleId && (
              <Col xs={24} md={6}>
                <div style={{ fontSize: 12, marginBottom: 4, fontWeight: 500 }}>Module *</div>
                <Select
                  style={{ width: '100%' }}
                  placeholder="Select Module"
                  value={selectedModuleId || undefined}
                  onChange={(val) => setSelectedModuleId(val)}
                  disabled={isBroadcasting || isStopping || !selectedCourseId}
                  showSearch
                  optionFilterProp="children"
                >
                  {modulesList.map((m) => (
                    <Option key={m.modules_id} value={m.modules_id}>
                      {m.modules_name}
                    </Option>
                  ))}
                </Select>
              </Col>
            )}
            <Col xs={8} md={propCourseId && propModuleId ? 4 : 3}>
              <div style={{ fontSize: 12, marginBottom: 4, fontWeight: 500, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Sl No. *</span>
                <span style={{ fontSize: 10, color: '#10b981', fontWeight: 600 }}>Auto-continued</span>
              </div>
              <InputNumber
                min={1}
                style={{ width: '100%' }}
                value={quickSlNo}
                onChange={(val) => setQuickSlNo(val || 1)}
                disabled={isBroadcasting || isStopping}
              />
            </Col>
            <Col xs={16} md={propCourseId && propModuleId ? 12 : 5}>
              <div style={{ fontSize: 12, marginBottom: 4, fontWeight: 500 }}>Session Title *</div>
              <Input
                placeholder="Enter live session title"
                value={quickTitle}
                onChange={(e) => setQuickTitle(e.target.value)}
                disabled={isBroadcasting || isStopping}
              />
            </Col>
            <Col xs={24} md={propCourseId && propModuleId ? 8 : 4}>
              <div style={{ fontSize: 12, marginBottom: 4, fontWeight: 500 }}>Instructor</div>
              <Select
                style={{ width: '100%' }}
                placeholder="Select Instructor"
                allowClear
                value={quickInstructorId}
                onChange={(val) => setQuickInstructorId(val)}
                disabled={isBroadcasting || isStopping}
                showSearch
                optionFilterProp="children"
              >
                {instructorsList.map((inst) => (
                  <Option key={inst.instructor_id} value={Number(inst.instructor_id)}>
                    {inst.instructor_name}
                  </Option>
                ))}
              </Select>
            </Col>
          </Row>
        </Card>
      )}

      {/* Main Row 1: Left = Live Broadcast Canvas + Scene Layouts; Right = Classroom Chat (Matching Classroom Stream!) */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <div
            style={{
              position: 'relative',
              background: '#0f172a',
              borderRadius: 12,
              overflow: 'hidden',
              border: isBroadcasting ? '2px solid #ef4444' : '1px solid var(--border-color)'
            }}
          >
            <canvas
              ref={previewCanvasRef}
              width={1280}
              height={720}
              style={{
                width: '100%',
                height: 'auto',
                aspectRatio: '16 / 9',
                display: 'block'
              }}
            />

            {/* Floating studio controls over preview */}
            <div
              style={{
                position: 'absolute',
                bottom: 12,
                left: 12,
                right: 12,
                background: 'rgba(15, 23, 42, 0.84)',
                backdropFilter: 'blur(6px)',
                padding: '8px 14px',
                borderRadius: 8,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 8
              }}
            >
              <Space wrap>
                <Tooltip title={isMicMuted ? 'Unmute Microphone' : 'Mute Microphone'}>
                  <Button
                    size="small"
                    type={isMicMuted ? 'primary' : 'default'}
                    danger={isMicMuted}
                    icon={isMicMuted ? <AudioMutedOutlined /> : <AudioOutlined />}
                    onClick={handleToggleMic}
                  />
                </Tooltip>
                <Tooltip title={isWebcamEnabled ? 'Hide Webcam' : 'Show Webcam'}>
                  <Button
                    size="small"
                    type={!isWebcamEnabled ? 'primary' : 'default'}
                    danger={!isWebcamEnabled}
                    icon={isWebcamEnabled ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                    onClick={handleToggleWebcam}
                    disabled={isStopping}
                  />
                </Tooltip>
                <Tooltip title="Mirror / Flip Webcam Horizontally">
                  <Button
                    size="small"
                    type={mirrorWebcam ? 'primary' : 'default'}
                    icon={<SwapOutlined />}
                    onClick={() => setMirrorWebcam((prev) => !prev)}
                  >
                    Mirror Cam
                  </Button>
                </Tooltip>
                <Button
                  size="small"
                  type="primary"
                  icon={<DesktopOutlined />}
                  onClick={acquireWindowCapture}
                  disabled={isStopping}
                >
                  {isWindowActive ? 'Change Window Capture' : 'Select Window Capture'}
                </Button>
              </Space>

              <Space size={12}>
                <span style={{ color: '#e2e8f0', fontSize: 12 }}>
                  Bitrate: <strong>{videoBitrate}</strong> / <strong>{audioBitrate}</strong>
                </span>
                {isBroadcasting && (
                  <span style={{ color: '#4ade80', fontSize: 12, fontWeight: 600 }}>
                    Streamed: {formatBytes(serverBytesSent)}
                  </span>
                )}
              </Space>
            </div>
          </div>

          {/* Interactive Broadcast Scene Layouts Bar */}
          <Card
            size="small"
            style={{ marginTop: 12, borderRadius: 10, border: '1px solid var(--border-color)' }}
            bodyStyle={{ padding: '10px 12px' }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 8,
                marginBottom: 8
              }}
            >
              <Space size={6}>
                <AppstoreOutlined style={{ color: '#8b5cf6' }} />
                <strong style={{ fontSize: 13 }}>Broadcast Scene Layouts</strong>
                <Tag color="purple" style={{ fontSize: 11, margin: 0 }}>
                  Live Switchable
                </Tag>
              </Space>

              <Space size={12} wrap>
                <Space size={4}>
                  <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>PiP Size:</span>
                  <Segmented
                    size="small"
                    options={['Small', 'Medium', 'Large']}
                    value={pipSize}
                    onChange={(val) => setPipSize(val)}
                  />
                </Space>
                <Space size={4}>
                  <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Title Banner:</span>
                  <Switch
                    size="small"
                    checked={showLowerThird}
                    onChange={(checked) => setShowLowerThird(checked)}
                  />
                </Space>
              </Space>
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
                gap: 8
              }}
            >
              {LAYOUT_PRESETS.map((preset) => {
                const isSelected = layoutPreset === preset.id;
                return (
                  <Tooltip key={preset.id} title={preset.desc}>
                    <div
                      onClick={() => handleSelectLayoutPreset(preset)}
                      style={{
                        cursor: 'pointer',
                        padding: '6px 4px',
                        borderRadius: 8,
                        textAlign: 'center',
                        border: isSelected
                          ? '2px solid #8b5cf6'
                          : '1px solid var(--border-color, #cbd5e1)',
                        background: isSelected
                          ? 'rgba(139, 92, 246, 0.12)'
                          : 'transparent',
                        transition: 'all 0.15s ease'
                      }}
                    >
                      {renderLayoutThumbnail(preset.id, isSelected)}
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: isSelected ? 700 : 500,
                          color: isSelected ? '#8b5cf6' : 'var(--text-color)',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {preset.name}
                      </div>
                    </div>
                  </Tooltip>
                );
              })}
            </div>
          </Card>
        </Col>

        {/* Right Column: Live Classroom Chat Window (Matching Classroom Stream!) */}
        <Col xs={24} lg={8}>
          <Card
            title={
              <span style={{ color: 'var(--text-color)', display: 'flex', alignItems: 'center', gap: 8 }}>
                Classroom Chat
                <span
                  style={{
                    display: 'inline-block',
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: '#10b981',
                    boxShadow: '0 0 0 0 rgba(16,185,129,0.6)',
                    animation: 'livePulse 1.8s infinite'
                  }}
                />
              </span>
            }
            style={{
              border: '1px solid var(--border-color)',
              borderRadius: 12,
              height: '100%',
              minHeight: 520,
              display: 'flex',
              flexDirection: 'column'
            }}
            bodyStyle={{
              flex: 1,
              overflowY: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              padding: 12
            }}
          >
            <div
              ref={chatContainerRef}
              style={{ flex: 1, overflowY: 'auto', marginBottom: 12, maxHeight: 440 }}
            >
              {chatMessages.length === 0 ? (
                <div
                  style={{
                    textAlign: 'center',
                    padding: '40px 12px',
                    color: 'var(--text-secondary)',
                    fontSize: 13
                  }}
                >
                  {currentSessionId
                    ? 'No chat messages yet. Say hello to your classroom!'
                    : 'Chat will connect automatically as soon as the session starts.'}
                </div>
              ) : (
                <List
                  dataSource={chatMessages}
                  renderItem={(item) => (
                    <List.Item style={{ border: 'none', padding: '6px 0' }}>
                      <List.Item.Meta
                        avatar={
                          <Avatar style={{ backgroundColor: '#7265e6' }} icon={<UserOutlined />} />
                        }
                        title={
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span
                              style={{
                                fontWeight: 600,
                                color: 'var(--text-color)',
                                fontSize: 13
                              }}
                            >
                              {item.user_name}
                            </span>
                            <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                              {item.sent_at
                                ? new Date(item.sent_at).toLocaleTimeString([], {
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })
                                : ''}
                            </span>
                          </div>
                        }
                        description={
                          <span style={{ color: 'var(--text-color)' }}>{item.chat_msg}</span>
                        }
                      />
                    </List.Item>
                  )}
                />
              )}
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <Input
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onPressEnter={handleSendChat}
                placeholder="Send message to classroom..."
                style={{
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid var(--border-color)',
                  color: 'var(--text-color)'
                }}
              />
              <Button type="primary" onClick={handleSendChat} icon={<SendOutlined />} />
            </div>
          </Card>
        </Col>
      </Row>

      {/* Main Row 2: Attending Participants (Student Live Webcams Grid — Matching Classroom Stream!) */}
      <Row gutter={16} style={{ marginTop: 16 }}>
        <Col span={24}>
          <Card
            title={
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  flexWrap: 'wrap',
                  gap: 10
                }}
              >
                <span style={{ color: 'var(--text-color)', display: 'flex', alignItems: 'center', gap: 8 }}>
                  Attending Participants (User Webcams)
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: 28,
                      height: 22,
                      borderRadius: 11,
                      background: 'rgba(16,185,129,0.15)',
                      border: '1px solid rgba(16,185,129,0.5)',
                      color: '#10b981',
                      fontSize: 13,
                      fontWeight: 700,
                      padding: '0 8px'
                    }}
                  >
                    {attendingViewers.length}
                  </span>
                  <span
                    style={{
                      display: 'inline-block',
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: '#10b981',
                      animation: 'livePulse 1.8s infinite'
                    }}
                  />
                </span>

                <Space size="middle">
                  {hiddenParticipantIds.length > 0 && (
                    <Tooltip title="Click to restore all hidden participant videos">
                      <Tag
                        color="volcano"
                        icon={<EyeOutlined />}
                        onClick={() => {
                          setHiddenParticipantIds([]);
                          message.success('All participant videos unhidden.');
                        }}
                        style={{ cursor: 'pointer', margin: 0, fontWeight: 600, padding: '3px 8px' }}
                      >
                        {hiddenParticipantIds.length} Hidden (Click to Unhide All)
                      </Tag>
                    </Tooltip>
                  )}
                  <Button
                    size="small"
                    icon={isParticipantsMinimized ? <DownOutlined /> : <UpOutlined />}
                    onClick={() => setIsParticipantsMinimized(!isParticipantsMinimized)}
                  >
                    {isParticipantsMinimized ? 'Expand' : 'Minimize'}
                  </Button>
                </Space>
              </div>
            }
            style={{ border: '1px solid var(--border-color)', borderRadius: 12 }}
          >
            {!isParticipantsMinimized && (
              <>
                {attendingViewers.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '18px 0', color: 'var(--text-secondary)' }}>
                    {hiddenParticipantIds.length > 0
                      ? 'All participant videos are currently hidden.'
                      : 'Waiting for participants to join the classroom stream...'}
                  </div>
                ) : (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                      gap: 16
                    }}
                  >
                    {attendingViewers.map((v) => {
                      const vId = v.login_id || v.session_viewers_id || v.id;
                      return (
                        <ParticipantWebcamCard
                          key={vId}
                          viewer={v}
                          onHide={handleHideParticipant}
                        />
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </Card>
        </Col>
      </Row>

      {/* Main Row 3: Studio Capture Devices, Bitrate & Local Recording Path Controls */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        {/* Card 1: Capture Devices */}
        <Col xs={24} md={8}>
          <Card
            size="small"
            style={{ borderRadius: 12, border: '1px solid var(--border-color)', height: '100%' }}
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Space>
                  <VideoCameraOutlined style={{ color: '#8b5cf6' }} />
                  <strong style={{ fontSize: 13 }}>1. Webcam, Window & Audio Capture</strong>
                </Space>
                <Button
                  size="small"
                  icon={<ReloadOutlined />}
                  onClick={refreshDevices}
                  disabled={isStopping}
                >
                  Refresh
                </Button>
              </div>
            }
            bodyStyle={{ display: 'flex', flexDirection: 'column', gap: 12 }}
          >
            {/* Webcam Capture Device */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <span style={{ fontSize: 12, fontWeight: 500 }}>Webcam Capture Device</span>
                <Tag color={isWebcamActive && isWebcamEnabled ? 'green' : 'default'} style={{ margin: 0, fontSize: 10 }}>
                  {isWebcamActive && isWebcamEnabled ? 'Active' : 'Off'}
                </Tag>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Select
                  style={{ flex: 1 }}
                  value={selectedVideoDeviceId || undefined}
                  onChange={(val) => {
                    setSelectedVideoDeviceId(val);
                    acquireWebcamCapture(val);
                  }}
                  disabled={isStopping}
                  placeholder="Select Webcam Device"
                >
                  {videoDevices.map((dev, idx) => (
                    <Option key={dev.deviceId || idx} value={dev.deviceId}>
                      {dev.label || `Webcam ${idx + 1}`}
                    </Option>
                  ))}
                  <Option value="none">None (Disable Webcam)</Option>
                </Select>
                <Button
                  icon={<VideoCameraOutlined />}
                  onClick={() => acquireWebcamCapture(selectedVideoDeviceId)}
                  disabled={isStopping || selectedVideoDeviceId === 'none'}
                  title="Start / Reconnect Webcam"
                />
              </div>
            </div>

            {/* Window Capture */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <span style={{ fontSize: 12, fontWeight: 500 }}>Window Capture</span>
                <Tag color={isWindowActive ? 'blue' : 'default'} style={{ margin: 0, fontSize: 10 }}>
                  {isWindowActive ? 'Sharing Active' : 'Not Selected'}
                </Tag>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Input
                  readOnly
                  value={isWindowActive ? windowLabel || 'Window / Screen Shared' : 'No Window Selected'}
                  style={{ flex: 1 }}
                />
                <Button
                  type="primary"
                  icon={<DesktopOutlined />}
                  onClick={acquireWindowCapture}
                  disabled={isStopping}
                  style={{ background: '#8b5cf6', borderColor: '#8b5cf6' }}
                >
                  {isWindowActive ? 'Change Window' : 'Select Window'}
                </Button>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 500 }}>Include Window / System Audio</span>
                <Switch
                  size="small"
                  checked={includeSystemAudio}
                  onChange={(checked) => setIncludeSystemAudio(checked)}
                  disabled={isStopping}
                />
              </div>
            </div>

            {/* Audio Capture Device (Microphone) */}
            <div>
              <div style={{ fontSize: 12, marginBottom: 4, fontWeight: 500 }}>
                Audio Capture Device (Microphone)
              </div>
              <Select
                style={{ width: '100%' }}
                value={selectedAudioDeviceId || undefined}
                onChange={(val) => {
                  setSelectedAudioDeviceId(val);
                  acquireAudioCapture(val);
                }}
                disabled={isStopping}
                placeholder="Default Microphone"
              >
                {audioDevices.map((dev, idx) => (
                  <Option key={dev.deviceId || idx} value={dev.deviceId}>
                    {dev.label || `Microphone ${idx + 1}`}
                  </Option>
                ))}
                <Option value="none">None (Mute Mic Input)</Option>
              </Select>
            </div>
          </Card>
        </Col>

        {/* Card 2: Classroom Stream Bitrate & Status */}
        <Col xs={24} md={8}>
          <Card
            size="small"
            style={{ borderRadius: 12, border: '1px solid var(--border-color)', height: '100%' }}
            title={
              <Space>
                <SettingOutlined style={{ color: '#2563eb' }} />
                <strong style={{ fontSize: 13 }}>2. Classroom Stream Bitrate & Telemetry</strong>
              </Space>
            }
            bodyStyle={{ display: 'flex', flexDirection: 'column', gap: 12 }}
          >
            <div>
              <div style={{ fontSize: 12, marginBottom: 4, fontWeight: 500 }}>Video Bitrate</div>
              <Select
                style={{ width: '100%' }}
                value={videoBitrate}
                onChange={(val) => setVideoBitrate(val)}
                disabled={isBroadcasting}
              >
                {VIDEO_BITRATE_OPTIONS.map((opt) => (
                  <Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Option>
                ))}
              </Select>
            </div>

            <div>
              <div style={{ fontSize: 12, marginBottom: 4, fontWeight: 500 }}>Audio Bitrate</div>
              <Select
                style={{ width: '100%' }}
                value={audioBitrate}
                onChange={(val) => setAudioBitrate(val)}
                disabled={isBroadcasting}
              >
                {AUDIO_BITRATE_OPTIONS.map((opt) => (
                  <Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Option>
                ))}
              </Select>
            </div>

            <Divider style={{ margin: '4px 0' }} />

            <Row gutter={8}>
              <Col span={12}>
                <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Server RTMP Recording</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#2563eb', marginTop: 2 }}>
                  {isBroadcasting ? `Recording (${chunksSent}s)` : 'Ready (Auto MP4)'}
                </div>
              </Col>
              <Col span={12}>
                <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Local System File</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#0d9488', marginTop: 2 }}>
                  {enableLocalRecording
                    ? isBroadcasting
                      ? `Saving (${formatBytes(localBytesWritten)})`
                      : 'Enabled'
                    : 'Disabled'}
                </div>
              </Col>
            </Row>
          </Card>
        </Col>

        {/* Card 3: Local System Recording Path */}
        <Col xs={24} md={8}>
          <Card
            size="small"
            style={{ borderRadius: 12, border: '1px solid var(--border-color)', height: '100%' }}
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Space>
                  <FolderOpenOutlined style={{ color: '#0d9488' }} />
                  <strong style={{ fontSize: 13 }}>3. Local System Recording Path</strong>
                </Space>
                <Switch
                  size="small"
                  checked={enableLocalRecording}
                  onChange={(checked) => setEnableLocalRecording(checked)}
                  disabled={isBroadcasting}
                />
              </div>
            }
            bodyStyle={{ display: 'flex', flexDirection: 'column', gap: 12 }}
          >
            {enableLocalRecording ? (
              <>
                <div>
                  <div style={{ fontSize: 12, marginBottom: 4, fontWeight: 500 }}>
                    Local Recording Format (.mkv / .flv)
                  </div>
                  <Select
                    style={{ width: '100%' }}
                    value={localRecordingFormat}
                    onChange={(val) => setLocalRecordingFormat(val)}
                    disabled={isBroadcasting}
                  >
                    <Option value="mkv">MKV (.mkv — Matroska H.264 + AAC)</Option>
                    <Option value="flv">FLV (.flv — Flash Video H.264 + AAC)</Option>
                  </Select>
                </div>

                <div>
                  <div style={{ fontSize: 12, marginBottom: 4, fontWeight: 500 }}>
                    Local Save Folder / Path
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Input
                      readOnly
                      value={
                        localDirName
                          ? `Selected: ${localDirName}`
                          : 'System Downloads Folder (Default)'
                      }
                    />
                    <Button
                      icon={<FolderOpenOutlined />}
                      onClick={handleSelectLocalPath}
                      disabled={isBroadcasting}
                    >
                      Select Path
                    </Button>
                  </div>
                </div>

                {localDirName && (
                  <div>
                    <div style={{ fontSize: 12, marginBottom: 4, fontWeight: 500 }}>
                      Optional Subfolder Path
                    </div>
                    <Input
                      placeholder="e.g. Classroom_Recordings/Module_1"
                      value={localSubfolderPath}
                      onChange={(e) => setLocalSubfolderPath(e.target.value)}
                      disabled={isBroadcasting}
                    />
                  </div>
                )}

                <div>
                  <div style={{ fontSize: 12, marginBottom: 4, fontWeight: 500 }}>
                    Local Recording Filename (.{localRecordingFormat})
                  </div>
                  <Input
                    value={localCustomFileName}
                    onChange={(e) => setLocalCustomFileName(e.target.value)}
                    placeholder={`session_recording.${localRecordingFormat}`}
                    disabled={isBroadcasting}
                  />
                </div>
              </>
            ) : (
              <div style={{ color: 'var(--text-secondary)', fontSize: 12, padding: '12px 0' }}>
                Local system recording is currently turned off. Enable the switch above to save a copy directly to your local disk.
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default WebBroadcastModal;
